import { useState } from 'react';
import Link from 'next/link';

import * as Dialog from '@radix-ui/react-dialog';
import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { Icons } from '@/components/icons';
import { Button } from '@/components/button';
import { VisuallyHidden } from '@/components/visually-hidden';
import { cn } from '@/utils/cn';
import { FEATURES } from '@/constants';

import { sidebarData } from '../../sidebar/data';
import { getIsQuantaPage, isSidebarItemActive } from '../../utils';

interface DropdownNavProps {
  pathname: string;
}

const DropdownNav: React.FC<DropdownNavProps> = ({ pathname }: DropdownNavProps) => {
  const { data: loginData } = useQuantaLogin({
    enabled: false,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isNavigationEnabled = !!loginData?.data.user.user_id;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setIsOpen(false);
  };

  const handleSubLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, parentHref: string) => {
    const isQuantaParent = getIsQuantaPage(parentHref);

    if (isQuantaParent && !isNavigationEnabled) {
      e.preventDefault();
      return;
    }

    setIsOpen(false);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <Button size='small' variant='tertiary' className='rounded-full !w-6 !h-6 bg-surface-secondary [&_svg]:size-4'>
          {isOpen ? <Icons.chevronUp /> : <Icons.chevronDown />}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content className='fixed left-0 top-[74px] w-[250px] bg-surface-secondary outline-none z-50'>
          <VisuallyHidden asChild>
            <Dialog.Title>Navigation Menu</Dialog.Title>
          </VisuallyHidden>
          <VisuallyHidden asChild>
            <Dialog.Description>Select a page to navigate to.</Dialog.Description>
          </VisuallyHidden>
          <div className='flex flex-col py-[10px]'>
            {sidebarData.map(({ href, name, subItems, icon }) => {
              const isDisabled =
                href === '/governance' || (href === '/liquidations' && !FEATURES.liquidations) || (href === '/swap' && !FEATURES.swap);
              const isActive = isSidebarItemActive(pathname, href);
              const isQuantaParent = getIsQuantaPage(href);
              const isRootActive = pathname === href;

              if (isDisabled) {
                return (
                  <div
                    key={href}
                    className={cn('flex items-center gap-3 px-8 py-3 font-medium text-text-secondary leading-18 cursor-not-allowed')}
                  >
                    {icon}
                    {name}
                  </div>
                );
              }

              return (
                <div key={href} className='flex flex-col'>
                  <Link
                    href={href}
                    className={cn(
                      'flex items-center justify-between w-full px-8 py-3 text-left font-medium text-text-primary/50 leading-18 hover:text-text-primary/80 transition-colors',
                      {
                        'text-text-primary cursor-default hover:text-text-primary font-bold': pathname === href,
                      },
                    )}
                    onClick={handleLinkClick}
                  >
                    <div className='flex items-center gap-3'>
                      <div className='flex items-center justify-center min-w-[26px]'>{icon}</div>
                      <span>{name}</span>
                    </div>
                    {subItems &&
                      (isActive ? (
                        <Icons.chevronDown
                          className={cn('size-4', { 'text-text-primary': isRootActive, 'text-text-primary/50': !isRootActive })}
                        />
                      ) : (
                        <Icons.chevronUp className='size-4 text-text-primary/50' />
                      ))}
                  </Link>
                  {subItems && isActive && (
                    <div className='flex flex-col ml-[38px]'>
                      {subItems.map(({ name: subName, href: subHref }, index) => {
                        const isDisabledSub = (isQuantaParent && !isNavigationEnabled) || subHref === '/overview/reserve';

                        return isDisabledSub ? (
                          <div
                            key={index}
                            className={cn('w-full px-8 py-3 text-left font-medium text-text-primary/50 leading-18 cursor-not-allowed')}
                          >
                            {subName}
                          </div>
                        ) : (
                          <Link
                            key={index}
                            href={subHref}
                            className={cn(
                              'w-full px-8 py-3 text-left font-medium text-text-primary/50 leading-18 hover:text-text-primary/80 transition-colors',
                              {
                                'text-text-primary cursor-default hover:text-text-primary font-bold': pathname === subHref,
                              },
                            )}
                            onClick={(e) => handleSubLinkClick(e, href)}
                          >
                            {subName}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DropdownNav;
